import { useEffect, useId, useState } from "react";
import type { MemoryExplanation, MemoryForgetReason, MemoryItemView, MemorySensitivity } from "../../shared/contracts";
import { api, errorText, isAbortError } from "../lib/api";
import { fmtDateTime, fmtDayWord, MEMORY_KIND_LABEL } from "../lib/format";
import { Badge, Button, cx, ErrorNotice, Loading } from "./ui";

export const SENSITIVITY_OPTIONS: { value: MemorySensitivity; label: string; hint: string }[] = [
  { value: "normal", label: "Normal", hint: "Ton agent peut s'en servir partout." },
  { value: "private", label: "Privé", hint: "Seulement quand tu lui parles directement." },
  { value: "care", label: "Sensible", hint: "Santé, argent, intime : avec tact, jamais de lui-même." },
];

const TRUST_LABEL: Record<MemoryExplanation["sources"][number]["trust"], string> = {
  user: "toi",
  agent: "l'agent",
  external: "source externe",
};

const FORGET_REASONS: { value: MemoryForgetReason; label: string }[] = [
  { value: "incorrect", label: "Incorrect" },
  { value: "too_private", label: "Trop privé" },
  { value: "outdated", label: "Plus d'actualité" },
];

const SOURCE_CHANNEL_LABEL: Record<string, string> = {
  chat: "Discussion",
  web: "Discussion",
  imessage: "iMessage",
  sms: "SMS",
  telegram: "Telegram",
  email: "Email",
  tool: "Outil",
  agent: "Agent",
  mission: "Mission",
  import: "Import",
};

/** "composio:gmail" → "Gmail", "imessage" → "iMessage". */
function sourceChannelLabel(channel: string): string {
  const known = SOURCE_CHANNEL_LABEL[channel];
  if (known) return known;
  const app = channel.startsWith("composio:") ? channel.slice("composio:".length) : channel;
  return app ? app.charAt(0).toUpperCase() + app.slice(1) : "Inconnu";
}

/** Small badge for private / care memories (nothing for normal ones). */
export function SensitivityBadge({ sensitivity }: { sensitivity: MemorySensitivity | undefined }) {
  if (sensitivity === "private") return <Badge tone="info">Privé</Badge>;
  if (sensitivity === "care") return <Badge tone="danger">Sensible</Badge>;
  return null;
}

/** "Confidentialité" radio group: Normal / Privé / Sensible. */
export function SensitivityPicker({ value, onChange }: { value: MemorySensitivity; onChange: (value: MemorySensitivity) => void }) {
  const name = useId();
  const hintId = useId();
  const current = SENSITIVITY_OPTIONS.find((o) => o.value === value) ?? SENSITIVITY_OPTIONS[0]!;
  return (
    <fieldset className="min-w-0">
      <legend className="mb-1.5 text-[13px] font-medium">Confidentialité</legend>
      <div className="flex flex-wrap gap-1.5" aria-describedby={hintId}>
        {SENSITIVITY_OPTIONS.map((o) => (
          <label
            key={o.value}
            className={cx(
              "flex h-9 cursor-pointer items-center gap-2 rounded-lg border px-3 text-[13px] transition",
              value === o.value ? "border-accent bg-accent-soft/60 font-medium" : "border-line bg-elev hover:border-line-strong",
            )}
          >
            <input type="radio" name={name} value={o.value} checked={value === o.value} onChange={() => onChange(o.value)} className="h-3.5 w-3.5" />
            {o.label}
          </label>
        ))}
      </div>
      <p id={hintId} className="mt-1.5 text-[12px] text-subtle">
        {current.hint}
      </p>
    </fieldset>
  );
}

/** "Pourquoi ?" panel: where a memory comes from and the older versions it replaced. */
export function WhyPanel({ id, now }: { id: string; now: number }) {
  const [data, setData] = useState<MemoryExplanation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    api("GET /api/memory/:id/why", { params: { id }, signal: controller.signal })
      .then((r) => setData(r))
      .catch((err: unknown) => {
        if (!isAbortError(err)) setError(errorText(err));
      });
    return () => controller.abort();
  }, [id, attempt]);

  if (error) return <ErrorNotice message={error} onRetry={() => setAttempt((a) => a + 1)} />;
  if (!data) return <Loading label="Je retrouve d'où ça vient…" />;

  return (
    <div className="space-y-3" aria-label="Pourquoi ton agent sait ça">
      {data.sources.length ? (
        <ul className="space-y-2">
          {data.sources.map((s, i) => (
            <li key={i} className="min-w-0">
              <blockquote className="rounded-[14px] bg-inset px-3 py-2.5 font-display text-[16px] leading-[1.4] whitespace-pre-line text-muted italic wrap-anywhere">« {s.quote} »</blockquote>
              <p className="mt-1.5 text-[12.5px] text-subtle">
                {s.at ? `${capitalizeFirst(fmtDayWord(s.at, now))} · ` : ""}
                {sourceChannelLabel(s.channel)}
                {s.trust !== "user" ? ` · ${TRUST_LABEL[s.trust] ?? s.trust}` : ""}
              </p>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[13.5px] text-muted">Ajouté directement, sans citation.</p>
      )}
      {data.history.length ? (
        <div>
          <p className="text-[12.5px] font-semibold text-subtle">Avant</p>
          <ul className="mt-1.5 space-y-1.5">
            {data.history.map((h) => (
              <HistoryLine key={h.id} item={h} now={now} />
            ))}
          </ul>
        </div>
      ) : null}
      {data.replacedBy ? (
        <p className="text-[13px] text-muted">
          Remplacé par : <span className="text-fg wrap-anywhere">{data.replacedBy.content}</span>
        </p>
      ) : null}
    </div>
  );
}

function capitalizeFirst(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function HistoryLine({ item, now }: { item: MemoryItemView; now: number }) {
  return (
    <li className="min-w-0">
      <p className="text-[13px] text-muted line-through decoration-subtle/60 wrap-anywhere">{item.content}</p>
      <p className="text-[11.5px] text-subtle">
        {MEMORY_KIND_LABEL[item.kind] ?? item.kind} · noté {fmtDateTime(item.recordedAt, now)}
        {item.validTo ? ` · valable jusqu'au ${fmtDateTime(item.validTo, now)}` : ""}
      </p>
    </li>
  );
}

/**
 * Forget confirmation: an optional reason (Incorrect / Trop privé / Plus d'actualité), then "Confirmer" or "Annuler".
 * The reason helps the agent avoid re-learning the same thing.
 */
export function ForgetConfirm({
  busy,
  onConfirm,
  onCancel,
}: {
  busy: boolean;
  onConfirm: (reason: MemoryForgetReason | null) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState<MemoryForgetReason | null>(null);
  const labelId = useId();
  return (
    <div className="w-full space-y-2 rounded-xl bg-sunken p-3" role="group" aria-labelledby={labelId}>
      <p id={labelId} className="text-[12.5px] text-muted">
        Effacé pour de bon. Pourquoi l'oublier ?
      </p>
      <div className="flex flex-wrap gap-1.5">
        {FORGET_REASONS.map((r) => (
          <Button
            key={r.value}
            size="sm"
            variant={reason === r.value ? "primary" : "secondary"}
            aria-pressed={reason === r.value}
            disabled={busy}
            onClick={() => setReason((cur) => (cur === r.value ? null : r.value))}
          >
            {r.label}
          </Button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="danger" busy={busy} onClick={() => onConfirm(reason)}>
          Confirmer
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>
          Annuler
        </Button>
      </div>
    </div>
  );
}
